/**
 * The job journal panel's view model (job page, "Runner journal").
 *
 * The data comes from the deploy-agent op `read-job-journal`
 * (internal/deploy/src/job-journal.ts) through the broker's SSO + operator
 * `POST /v1/fleet/diagnose` relay: the journal lines of ONE job id from the
 * `pure-runner` (or `pure-publish`) unit of a fleet box, already filtered by
 * the agent on the parsed `jobId` field and already redacted — no sudo
 * (plans/2026-10-14-publish-checklist-and-job-journal.md).
 *
 * The job record carries no runner host (the api's `job.running` event has a
 * runner_id but it is not persisted), so the panel asks EVERY connected
 * runner box and shows whichever has lines for the job.
 */

export const JOURNAL_UNITS = ["pure-runner", "pure-publish"] as const;
export type JournalUnit = (typeof JOURNAL_UNITS)[number];

/** The slice of GET /v1/fleet/overview a box needs for target selection. */
export interface FleetBoxLite {
  box_id: string;
  hostname: string;
  connected: boolean;
  installed_components?: Record<string, string>;
  entities?: Array<{ entity: string }>;
}

/** Boxes that can have this unit's journal: connected, and running the
 *  unit's component (runner: a `runner:*` entity or the installed
 *  component; publish: the installed component). */
export function journalTargets(
  boxes: readonly FleetBoxLite[],
  unit: JournalUnit,
): FleetBoxLite[] {
  const component = unit === "pure-runner" ? "runner" : "publish";
  return boxes.filter(
    (b) =>
      b.connected &&
      (component in (b.installed_components ?? {}) ||
        (component === "runner" &&
          (b.entities ?? []).some((e) => e.entity.startsWith("runner:")))),
  );
}

export interface JournalEntry {
  ts_ms: number;
  priority: number;
  message: string;
}

/** One line of the merged view. */
export interface JournalLine extends JournalEntry {
  host: string;
  /** pino fields, when the message is a JSON log line. */
  level: string | null;
  msg: string | null;
  guestConsole: boolean;
  /** The text shown: "<time> <host> <LEVEL> <msg> <extra fields>". */
  text: string;
}

const PINO_LEVELS: Record<number, string> = {
  10: "TRACE",
  20: "DEBUG",
  30: "INFO",
  40: "WARN",
  50: "ERROR",
  60: "FATAL",
};

const SYSLOG_PRIORITY: Record<number, string> = {
  0: "EMERG",
  1: "ALERT",
  2: "CRIT",
  3: "ERR",
  4: "WARN",
  5: "NOTICE",
  6: "INFO",
  7: "DEBUG",
};

export function isGuestConsoleMsg(msg: string | null): boolean {
  return !!msg && /guest console/i.test(msg);
}

/** "2026-10-14 09:33:41.512" in UTC. */
export function fmtTs(ms: number): string {
  if (!ms) return "—";
  return new Date(ms).toISOString().replace("T", " ").replace("Z", "");
}

/** Renders one agent entry: pino JSON lines as `LEVEL msg {extras}` (the
 *  job id, pid and hostname are the same on every line and dropped), other
 *  lines verbatim. */
export function toLine(host: string, e: JournalEntry): JournalLine {
  let level: string | null = null;
  let msg: string | null = null;
  let body = e.message;
  const s = e.message.trimStart();
  if (s.startsWith("{")) {
    try {
      const o = JSON.parse(s) as Record<string, unknown>;
      const lv = typeof o["level"] === "number" ? o["level"] : null;
      level = lv !== null ? (PINO_LEVELS[lv] ?? String(lv)) : null;
      msg = typeof o["msg"] === "string" ? o["msg"] : null;
      const {
        level: _l,
        time: _t,
        pid: _p,
        hostname: _h,
        jobId: _j,
        msg: _m,
        ...extra
      } = o;
      void _l;
      void _t;
      void _p;
      void _h;
      void _j;
      void _m;
      const extras = Object.keys(extra).length
        ? ` ${JSON.stringify(extra)}`
        : "";
      body = `${level ?? ""} ${msg ?? ""}${extras}`.trim();
    } catch {
      /* truncated JSON: show it as is */
    }
  }
  if (level === null) level = SYSLOG_PRIORITY[e.priority] ?? null;
  return {
    ...e,
    host,
    level,
    msg,
    guestConsole: isGuestConsoleMsg(msg),
    text: `${fmtTs(e.ts_ms)} ${host} ${body}`,
  };
}

/** The agent's op result (`payload`) → lines; [] when malformed. */
export function linesFromPayload(
  host: string,
  payload: unknown,
): JournalLine[] {
  if (!payload || typeof payload !== "object") return [];
  const entries = (payload as { entries?: unknown }).entries;
  if (!Array.isArray(entries)) return [];
  return entries
    .filter(
      (e): e is JournalEntry =>
        !!e &&
        typeof e === "object" &&
        typeof (e as JournalEntry).message === "string" &&
        typeof (e as JournalEntry).ts_ms === "number",
    )
    .map((e) => toLine(host, e));
}

/** Merge several hosts' lines chronologically (stable). */
export function mergeLines(perHost: readonly JournalLine[][]): JournalLine[] {
  return perHost
    .flat()
    .map((l, i) => ({ l, i }))
    .sort((a, b) => a.l.ts_ms - b.l.ts_ms || a.i - b.i)
    .map(({ l }) => l);
}

export interface JournalFilter {
  query: string;
  /** "all" | "console" (guest console only) | "no-console" */
  console: "all" | "console" | "no-console";
  /** Minimum severity: only WARN and worse. */
  problemsOnly: boolean;
}

const BAD = new Set([
  "WARN",
  "ERROR",
  "FATAL",
  "ERR",
  "CRIT",
  "ALERT",
  "EMERG",
]);

export function filterLines(
  lines: readonly JournalLine[],
  f: JournalFilter,
): JournalLine[] {
  const q = f.query.trim().toLowerCase();
  return lines.filter((l) => {
    if (f.console === "console" && !l.guestConsole) return false;
    if (f.console === "no-console" && l.guestConsole) return false;
    if (f.problemsOnly && !(l.level && BAD.has(l.level))) return false;
    return !q || l.text.toLowerCase().includes(q);
  });
}

/** What the copy button puts on the clipboard: the visible lines. */
export function copyText(lines: readonly JournalLine[]): string {
  return lines.map((l) => l.text).join("\n");
}

/** The op args for a job. `sinceMs` backs off 5 min before the job's
 *  creation (queue/claim lines), clamped to the agent's accepted range; no
 *  job time → the agent's own default look-back. */
export function journalArgs(
  jobId: string,
  unit: JournalUnit,
  lines: number,
  jobCreatedAtMs: number | null | undefined,
): Record<string, unknown> {
  const args: Record<string, unknown> = { jobId, unit, lines };
  if (jobCreatedAtMs && jobCreatedAtMs > 1_577_836_800_000 + 300_000)
    args["sinceMs"] = Math.floor(jobCreatedAtMs - 300_000);
  return args;
}

/** One status line under the panel. */
export function summaryText(r: {
  shown: number;
  total: number;
  hosts: number;
  truncated: string[];
  errors: string[];
}): string {
  const parts = [
    `${r.shown} of ${r.total} line${r.total === 1 ? "" : "s"} from ${r.hosts} host${r.hosts === 1 ? "" : "s"}`,
  ];
  if (r.truncated.length)
    parts.push(
      `older lines not shown (${[...new Set(r.truncated)].join(", ")} limit)`,
    );
  if (r.errors.length) parts.push(r.errors.join("; "));
  return parts.join(" · ");
}
